<script setup>
import { useRoute } from "vue-router"
import { useMutation } from "@vue/apollo-composable"
import { ref } from "vue"
import ADD_USER_REFERENCES from "@/graphql/Auth/AddUserDetailReferencesMutation.gql"

const route = useRoute()

const token = ref(route.params.token)

const companyName1 = ref(null)
const contact1 = ref(null)
const email1 = ref(null)
const companyName2 = ref(null)
const contact2 = ref(null)
const email2 = ref(null)
const loadingMessage = ref(false)

const { mutate: addUserReferences, loading: registeringLoading } =
  useMutation(ADD_USER_REFERENCES)

const submit = () => {
  const combinedData = {
    reference1: {
      name: companyName1.value,
      contact: contact1.value,
      email: email1.value,
    },
    reference2: {
      name: companyName2.value,
      contact: contact2.value,
      email: email2.value,
    },
  }
  const combinedDataJson = JSON.stringify(combinedData)

  addUserReferences({
    input: {
      token: token.value,
      references: combinedDataJson,
      platformTag: "ytz",
    },
  })
    .then(response => {
      loadingMessage.value = response.data.addUserDetailReferences.status
    })
    .catch(error => {
      loadingMessage.value = error.data.addUserDetailReferences.status
    })
}
</script>

<template>
  <div class="ref-page">
    <!-- Accent bar at top -->
    <div class="accent-bar"></div>

    <div class="ref-page-inner">
      <!-- Logo -->
      <div class="mb-10">
        <a href="https://www.ytz.com/">
          <img
            class="mx-auto h-14 w-auto rounded-[0.25rem]"
            src="@/assets/images/ytz-logo.svg"
            alt="YTZ company logo"
          />
        </a>
      </div>

      <!-- Success / Error state -->
      <template v-if="loadingMessage">
        <div class="ref-card">
          <div class="flex flex-col items-center justify-center py-12 px-6 text-center">
            <div
              class="result-icon"
              :class="{
                'result-icon--success': loadingMessage === 'REFERENCE_CREATED',
                'result-icon--error': loadingMessage !== 'REFERENCE_CREATED',
              }"
            >
              <svg v-if="loadingMessage === 'REFERENCE_CREATED'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-8 h-8"><polyline points="20 6 9 17 4 12"></polyline></svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-8 h-8"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </div>
            <h2 class="text-xl font-medium mt-6 mb-2">
              {{ loadingMessage === "REFERENCE_CREATED" ? "Thank you!" : "Something went wrong" }}
            </h2>
            <p class="text-base-content/60 text-sm max-w-[280px]">
              {{
                loadingMessage === "REFERENCE_CREATED"
                  ? "Your application will be reconsidered with this additional information."
                  : "Please reach out to us at contact@ytz.com for assistance."
              }}
            </p>
          </div>
        </div>
      </template>

      <!-- Form -->
      <template v-else>
        <form @submit.prevent="submit">
          <!-- Header card -->
          <div class="ref-card ref-card--header">
            <h1 class="ref-title">Professional References</h1>
            <p class="ref-subtitle">
              Please provide two professional references below. We'll verify them using the name and email from your application.
            </p>
          </div>

          <!-- Reference sections -->
          <div class="ref-card ref-card--form">
            <!-- Reference 1 -->
            <div class="ref-section">
              <div class="ref-section-header">
                <span class="ref-number">1</span>
                <span class="ref-label">First Reference</span>
              </div>
              <div class="ref-fields">
                <XInput
                  id="company-name1"
                  label="Company Name"
                  v-model="companyName1"
                  type="text"
                  required="true"
                />
                <XInput
                  id="contact-name1"
                  label="Contact Name"
                  v-model="contact1"
                  type="text"
                  required="true"
                />
                <XInput
                  id="contact-email1"
                  label="Company Email"
                  v-model="email1"
                  type="email"
                  required="true"
                />
              </div>
            </div>

            <div class="ref-divider"></div>

            <!-- Reference 2 -->
            <div class="ref-section">
              <div class="ref-section-header">
                <span class="ref-number">2</span>
                <span class="ref-label">Second Reference</span>
              </div>
              <div class="ref-fields">
                <XInput
                  id="company-name2"
                  label="Company Name"
                  v-model="companyName2"
                  type="text"
                  required="true"
                />
                <XInput
                  id="contact-name2"
                  label="Contact Name"
                  v-model="contact2"
                  type="text"
                  required="true"
                />
                <XInput
                  id="contact-email2"
                  label="Company Email"
                  v-model="email2"
                  type="email"
                  required="true"
                />
              </div>
            </div>

          </div>

          <!-- Submit -->
          <div class="ref-card ref-card--submit">
            <div class="flex items-center justify-between gap-4">
              <p class="text-base-content/40 text-xs leading-relaxed hidden sm:block">
                Providing references does not guarantee approval.
              </p>
              <XButton
                color="accent"
                type="submit"
                :isLoading="registeringLoading"
                :disabled="loadingMessage"
                class="ref-submit-btn"
              >Submit References</XButton>
            </div>
          </div>
        </form>
      </template>
    </div>
  </div>
</template>

<style scoped>
.ref-page {
  @apply min-h-screen w-full;
  background:
    radial-gradient(ellipse at 20% 0%, oklch(0.55 0.1 230 / 0.06) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 100%, oklch(0.55 0.1 230 / 0.04) 0%, transparent 50%);
}

.accent-bar {
  @apply h-1 w-full;
  background: linear-gradient(to right, #23c2ec, #2898db);
}

.ref-page-inner {
  @apply max-w-lg mx-auto px-4 py-12 sm:px-6;
}

.ref-card {
  @apply rounded-lg border border-neutral/40 bg-base-200;
}

.ref-card--header {
  @apply px-6 py-8 sm:px-8 text-center mb-3;
}

.ref-card--form {
  @apply px-6 py-6 sm:px-8 sm:py-8;
}

.ref-card--submit {
  @apply px-6 py-4 sm:px-8 mt-3;
}

.ref-title {
  @apply text-xl sm:text-2xl font-medium tracking-wide mb-3;
}

.ref-subtitle {
  @apply text-sm text-base-content/80 leading-relaxed max-w-sm mx-auto;
}

.ref-section {
  @apply flex flex-col gap-5;
}

.ref-section-header {
  @apply flex items-center gap-3;
}

.ref-number {
  @apply w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium shrink-0;
  background: linear-gradient(135deg, #23c2ec, #2898db);
  color: white;
}

.ref-label {
  @apply text-sm font-medium tracking-wide text-base-content/70;
}

.ref-fields {
  @apply flex flex-col gap-5 pl-10;
}

.ref-divider {
  @apply my-6 border-t border-neutral/30;
}

.ref-submit-btn {
  @apply px-6 shrink-0 !text-white;
}

.result-icon {
  @apply w-16 h-16 rounded-full flex items-center justify-center;
}

.result-icon--success {
  @apply bg-success/10 text-success;
}

.result-icon--error {
  @apply bg-error/10 text-error;
}
</style>
